import React, { useEffect, useRef, useState } from "react";

import LocalPrintshopOutlinedIcon from "@mui/icons-material/LocalPrintshopOutlined";
import { Box, Typography } from "@mui/material";
import { Form, Formik } from "formik";
import { useNavigate, useParams } from "react-router-dom";
import { useReactToPrint } from "react-to-print";

import {
  CreateCondition,
  getConditionById,
  UpdateConditionById,
} from "../../../Api/Services/ConditionsServices";
import { getPetNameByWorkspaceId } from "../../../Api/Services/PetServices";
import { useAuth } from "../../../ContextProvider/AuthProvider/AuthProvider";
import {
  FormHeaderBox,
  FormHeaderTitle,
  Body,
  Column,
  ButtonBoxStyle,
  CancelButton,
} from "../../../Css";
import { ConditionsFormValidation } from "../../../From/Validation";
import AutoComplete from "../../Common/AutoComplete";
import CustomButton from "../../Common/Button";
import FormGroup from "../../Common/FormGroup";
import FormLayout from "../../Common/FormHeader";
import InputField from "../../Common/InputField";
import { showSnackbar } from "../../Common/Snackbar";
import TextArea from "../../Common/TextArea";
import { useTheme } from "@mui/material/styles";

import "../../Item-List/GlobalItemList.css";

const defaultValue = {
  conditions: "",
  condition_note: "",
  pet_id: "",
};

const ConditionFormSection = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const { workspaceId: w_id } = useAuth();
  const theme = useTheme();
  const componentPDF = useRef();

  // Determine if this is edit mode
  const isEditMode = !!id;

  const [initialValue, setInitialValue] = useState(defaultValue);
  const [items, setItems] = useState([]);

  // Load existing data for edit mode
  const loadConditionDetails = async () => {
    if (!isEditMode) return;
    
    try {
      const { data } = await getConditionById(id);
      setInitialValue({
        ...initialValue,
        conditions: data?.data?.[0]?.conditions,
        condition_note: data?.data?.[0]?.condition_note,
        pet_id: data?.data?.[0]?.pet_id,
      });
    } catch (error) {
      showSnackbar({
        type: "error",
        message: "Failed to load condition details.",
      });
    }
  };

  const getPetNames = async () => {
    try {
      const { data } = await getPetNameByWorkspaceId(w_id);
      const filteredData = data?.data?.map((item) => {
        return {
          value: item?.id,
          label: item?.name,
        };
      });
      setItems(filteredData);
    } catch (error) {
      showSnackbar({
        type: "error",
        message: "Failed to load pet names.",
      });
    }
  };

  useEffect(() => {
    getPetNames();
    if (isEditMode) {
      loadConditionDetails();
    }
  }, [w_id, isEditMode]);

  // Create new condition
  const createConditionData = async (values) => {
    const value = {
      conditions: values?.conditions,
      condition_note: values?.condition_note,
      pet_id: values?.pet_id,
    };
    
    try {
      const conditionRes = await CreateCondition(value);
      if (conditionRes?.data?.success) {
        showSnackbar({
          type: "success",
          message: "Condition information added successfully",
        });
      }
    } catch (err) {
      showSnackbar({
        type: "error",
        message: "Condition information added failed",
      });
      console.error(err);
      throw err;
    }
  };

  // Update existing condition
  const updateConditionData = async (values) => {
    const value = {
      conditions: values?.conditions,
      condition_note: values?.condition_note,
      pet_id: values?.pet_id,
    };
    
    try {
      const res = await UpdateConditionById(id, value);
      if (res?.data?.success) {
        showSnackbar({
          type: "success",
          message: "Condition information updated successfully",
        });
      }
    } catch (error) {
      showSnackbar({
        type: "error",
        message: "Condition information update failed",
      });
      throw error;
    }
  };

  const handleSubmit = async (values, { isSubmitting }) => {
    try {
      if (isEditMode) {
        await updateConditionData(values);
      } else {
        await createConditionData(values);
      }
    } catch (error) {
      console.error("Failed to process condition:", error);
    } finally {
      isSubmitting(false);
    }
  };

  // Handle print functionality
  const handlePrint = useReactToPrint({
    content: () => componentPDF.current,
    documentTitle: "Condition Information",                                                                                                                                                                   
    pageStyle: `
      @page { size: A3; margin: 200px; }
      body { margin: 100px; }
      .print-text-field { font-size: 14px; }
    `,
  });

  return (
    <FormLayout title={isEditMode ? "Edit Condition Information" : "Condition Information"}>
      <Formik
        initialValues={initialValue}
        onSubmit={handleSubmit}
        validationSchema={ConditionsFormValidation}
        enableReinitialize
      >
        {({ getFieldProps, values, isSubmitting }) => (
          <Form>
            <Box sx={FormHeaderBox(theme.palette.primary.main)}>
              <Box sx={{ borderBottom: `1px solid ${theme.palette.primary.main}`, width: "100%" }}>
                <Typography variant="h6" sx={FormHeaderTitle}>
                  Condition Details
                </Typography>
              </Box>
              <div className="scroll2" ref={componentPDF} style={{ width: "100%" }}>
                <Body style={{ gap: "10px 20px" }}>
                  <Column>
                    <FormGroup name="pet_id" label={"Pet Name"} requiredField>
                      <AutoComplete
                        {...getFieldProps("pet_id")}
                        options={items}
                        value={isEditMode ? items.find(option => option.value === values.pet_id) : undefined}
                        textFieldStyle={{
                          width: {
                            xs: 250,
                            sm: 443,
                            md: 443,
                            lg: 443,
                            xl: 443,
                          },
                          height: "35px",
                        }}
                      />
                    </FormGroup>
                  </Column>
                  <Column>
                    <FormGroup
                      name="conditions"
                      label={"Condition"}
                      requiredField
                    >
                      <InputField {...getFieldProps("conditions")} />
                    </FormGroup>
                  </Column>
                </Body>
                <Body style={{ gap: "10px 20px", marginTop: "10px" }}>
                  <Column>
                    <FormGroup name="condition_note" label={"Notes"}>
                      <TextArea
                        rows={7}
                        maxLength={300}
                        {...getFieldProps("condition_note")}
                      />
                    </FormGroup>
                  </Column>
                </Body>
              </div>
              <Box sx={ButtonBoxStyle(`${theme.palette.primary.main}`)}>
                <Box display="flex">
                  <CustomButton
                    label="Cancel"
                    type="button"
                    style={CancelButton(`${theme.palette.primary.main}`)}
                    onClick={() => navigate(-1)}
                  />
                  <CustomButton
                    label={isSubmitting ? (isEditMode ? "Saving..." : "Submitting...") : (isEditMode ? "Save" : "Submit")}
                    disabled={isSubmitting}
                    type="submit"
                  />
                  {isEditMode && (
                    <CustomButton
                      startIcon={<LocalPrintshopOutlinedIcon />}
                      onClick={handlePrint}
                    />
                  )}
                </Box>
              </Box>
            </Box>
          </Form>
        )}
      </Formik>
    </FormLayout>
  );
};

export default ConditionFormSection;
